// SalonFlow UI kit: small, accessible, phone-first components (black & white + one accent).
import {
  type ButtonHTMLAttributes, cloneElement, createContext, isValidElement, type ReactElement, type ReactNode, useCallback,
  useContext, useEffect, useId, useRef, useState,
} from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({ variant = 'primary', loading = false, block = false, className = '', children, disabled, ...rest }:
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; loading?: boolean; block?: boolean }) {
  return (
    <button
      type="button"
      {...rest}
      className={`btn btn-${variant}${block ? ' btn-block' : ''} ${className}`.trim()}
      disabled={disabled || loading}
      aria-busy={loading ? 'true' : undefined}
    >
      {loading && <Spinner small />}
      <span>{children}</span>
    </button>
  );
}

export function Spinner({ small = false, label = 'กำลังโหลด' }: { small?: boolean; label?: string }) {
  return <span className={`spinner${small ? ' spinner-sm' : ''}`} role="status" aria-label={label} />;
}

export function Loading({ text = 'กำลังโหลด…' }: { text?: string }) {
  return <div className="loading"><Spinner /><p>{text}</p></div>;
}

export function Field({ label, error, hint, children }: { label: string; error?: string | null; hint?: string; children: ReactNode }) {
  const id = useId();
  const msgId = `${id}-msg`;
  const child = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
      id,
      'aria-invalid': error ? 'true' : undefined,
      'aria-describedby': error || hint ? msgId : undefined,
    })
    : children;
  return (
    <div className={`field${error ? ' field-error' : ''}`}>
      <label htmlFor={id}>{label}</label>
      {child}
      {error ? <p id={msgId} className="field-msg error">{error}</p> : hint ? <p id={msgId} className="field-msg">{hint}</p> : null}
    </div>
  );
}

export function Banner({ kind = 'info', children, action }: {
  kind?: 'info' | 'warn' | 'error' | 'success'; children: ReactNode; action?: { label: string; onClick?: () => void; href?: string };
}) {
  return (
    <div className={`banner banner-${kind}`} role={kind === 'error' || kind === 'warn' ? 'alert' : 'status'}>
      <div className="banner-text">{children}</div>
      {action && (action.href
        ? <a className="btn btn-secondary btn-sm" href={action.href}>{action.label}</a>
        : <Button variant="secondary" className="btn-sm" onClick={action.onClick}>{action.label}</Button>)}
    </div>
  );
}

export function Empty({ title, text, action }: { title: string; text?: string; action?: { label: string; onClick?: () => void; href?: string } }) {
  return (
    <div className="empty">
      <div className="empty-mark" aria-hidden="true">✦</div>
      <p className="empty-title">{title}</p>
      {text && <p className="empty-text">{text}</p>}
      {action && (action.href
        ? <a className="btn btn-primary" href={action.href}>{action.label}</a>
        : <Button onClick={action.onClick}>{action.label}</Button>)}
    </div>
  );
}

export function Card({ children, className = '', onClick, as = 'div' }: { children: ReactNode; className?: string; onClick?: () => void; as?: 'div' | 'button' }) {
  if (as === 'button' || onClick) {
    return <button type="button" className={`card card-button ${className}`} onClick={onClick}>{children}</button>;
  }
  return <div className={`card ${className}`}>{children}</div>;
}

export function Chip({ children, tone = 'default' }: { children: ReactNode; tone?: 'default' | 'dark' | 'accent' | 'muted' | 'ok' }) {
  return <span className={`chip chip-${tone}`}>{children}</span>;
}

export function Segmented<T extends string>({ options, value, onChange, label }: {
  options: { value: T; label: string }[]; value: T; onChange: (v: T) => void; label: string;
}) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value}
          className={o.value === value ? 'active' : ''} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

function useEscape(open: boolean, onEscape: () => void) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onEscape(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onEscape]);
}

export function Dialog({ open, title, children, confirmLabel = 'ยืนยัน', cancelLabel = 'ยกเลิก', onConfirm, onCancel, danger = false,
  loading = false, confirmDisabled = false }: {
  open: boolean; title: string; children?: ReactNode; confirmLabel?: string; cancelLabel?: string; onConfirm: () => void;
  onCancel: () => void; danger?: boolean; loading?: boolean; confirmDisabled?: boolean;
}) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  useEscape(open, onCancel);
  useEffect(() => { if (open) ref.current?.querySelector<HTMLElement>('input, textarea, select, button')?.focus(); }, [open]);
  if (!open) return null;
  return (
    <div className="overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref}>
        <h2 id={titleId}>{title}</h2>
        {children && <div className="dialog-body">{children}</div>}
        <div className="dialog-actions">
          <Button variant="ghost" onClick={onCancel} disabled={loading}>{cancelLabel}</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading} disabled={confirmDisabled}>{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
}

export function Sheet({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: ReactNode }) {
  const titleId = useId();
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <div className="overlay overlay-sheet" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="sheet-head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="icon-btn" aria-label="ปิด" onClick={onClose}>✕</button>
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </div>
  );
}

export function Page({ title, back, children, actions }: { title: string; back?: () => void; children: ReactNode; actions?: ReactNode }) {
  useEffect(() => { document.title = title; }, [title]);
  return (
    <main className="page">
      <header className="page-head">
        {back && <button type="button" className="icon-btn" aria-label="ย้อนกลับ" onClick={back}>‹</button>}
        <h1>{title}</h1>
        {actions && <div className="page-actions">{actions}</div>}
      </header>
      {children}
    </main>
  );
}

// ---------------------------------------------------------------- toasts

const ToastCtx = createContext<(text: string, kind?: 'info' | 'error' | 'success') => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<{ id: number; text: string; kind: string }[]>([]);
  const show = useCallback((text: string, kind: 'info' | 'error' | 'success' = 'info') => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, text, kind }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 4000);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => <div key={t.id} className={`toast toast-${t.kind}`} role="status">{t.text}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return useContext(ToastCtx);
}
